import React from 'react';
import { isSameDay, format, isToday as isDateToday } from 'date-fns';
import AppointmentCard from './AppointmentCard';

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7);
const HOUR_HEIGHT = 64;
const START_HOUR = 7;

function layoutOverlaps(appts) {
  const sorted = [...appts].sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const columns = [];
  for (const appt of sorted) {
    let placed = false;
    for (let col = 0; col < columns.length; col++) {
      const lastInCol = columns[col][columns[col].length - 1];
      if (appt.start >= lastInCol.end) {
        columns[col].push(appt);
        appt._col = col;
        placed = true;
        break;
      }
    }
    if (!placed) {
      appt._col = columns.length;
      columns.push([appt]);
    }
  }
  const totalCols = columns.length || 1;
  for (const appt of sorted) appt._totalCols = totalCols;
  return sorted;
}

function getPosition(appt) {
  const startMin = appt.start.getHours() * 60 + appt.start.getMinutes();
  const endMin = appt.end.getHours() * 60 + appt.end.getMinutes();
  const top = ((startMin - START_HOUR * 60) / 60) * HOUR_HEIGHT;
  const height = Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, 28);
  const col = appt._col || 0;
  const totalCols = appt._totalCols || 1;
  const width = `calc(${100 / totalCols}% - 4px)`;
  const left = `calc(${(col / totalCols) * 100}% + 2px)`;
  return { top, height, width, left };
}

export default function DayView({ date, appointments, onSelectAppointment, typesMap = {} }) {
  const dayAppts = layoutOverlaps(appointments.filter(a => isSameDay(a.start, date)));
  const today = isDateToday(date);
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nowTop = ((nowMin - START_HOUR * 60) / 60) * HOUR_HEIGHT;
  const showNowLine = today && nowMin >= START_HOUR * 60 && nowMin <= (START_HOUR + HOURS.length) * 60;

  return (
    <div className="px-4 md:px-7">
      <div className="relative" style={{ height: HOURS.length * HOUR_HEIGHT }}>
        {/* Hour grid */}
        {HOURS.map(h => (
          <div key={h} className="absolute left-0 right-0" style={{ top: (h - START_HOUR) * HOUR_HEIGHT, height: HOUR_HEIGHT }}>
            <div className="flex h-full">
              <div className="w-[56px] md:w-[68px] flex-shrink-0 text-[11px] font-medium text-muted-c text-right pr-3 -translate-y-[7px]">
                {h === 0 ? '12 AM' : h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`}
              </div>
              <div className="flex-1 border-t border-theme relative">
                <div className="absolute left-0 right-0 border-t border-theme/30" style={{ top: HOUR_HEIGHT / 2 }} />
              </div>
            </div>
          </div>
        ))}

        {/* Current time line */}
        {showNowLine && (
          <div className="absolute z-[3] flex items-center" style={{ top: nowTop, left: 56, right: 0 }}>
            <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444] -ml-[5px]" />
            <div className="flex-1 h-[2px] bg-[#EF4444]" />
          </div>
        )}

        {/* Appointments */}
        <div className="absolute z-[2]" style={{ top: 0, bottom: 0, left: 68, right: 12 }}>
          {dayAppts.map(appt => {
            const pos = getPosition(appt);
            return (
              <div
                key={appt.id}
                className="absolute overflow-hidden"
                style={{ top: pos.top, height: pos.height, width: pos.width, left: pos.left }}
              >
                <AppointmentCard appointment={appt} compact onClick={onSelectAppointment} typesMap={typesMap} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
